import { AbstractBlockFactory } from '../block/AbstractBlockFactory';
import {
    BlockType,
    ComponentSettings,
    ComponentSettingsChartMeasureField,
    ContentSettings,
    DatasetField,
    DatasetFieldValueType,
    DrillTo,
    generateMeasureFieldId,
    IComponentProperties,
    NewBlockContextType,
    OrderDirection,
} from '@report-content';
import { AssocArray, YNValues } from 'tools/types';
import { IconType } from 'components/common/icon/IconHandler';
import { FormDataAPIType } from 'components/common/form';
import React from 'react';

export const MeasureFieldSortingContext = React.createContext<MeasureFieldSortingContextType | null>(null);

export class BlockFactoryLineChart extends AbstractBlockFactory<ComponentSettingsLineChart> {
    public getNewBlock({
        nextUId,
        nextSortOrder,
        datasetFields,
    }: NewBlockContextType): BlockType<ComponentSettingsLineChart> {
        const uid = 'block_uid_' + nextUId;
        return {
            // TODO:
            component: {
                datasetRendererBlockComponentId: nextUId,
                datasetRendererBlockId: nextUId,
                datasetRendererComponentId: nextUId,
                displayOrder: nextSortOrder,
                internalName: 'line',
                settings: {
                    uid: uid,
                    title: '',
                    height: 600,
                    show_in_viewer: 'Y',
                    is_preview_ind: 'N',
                    include_in_email: 'Y',
                    is_collapsed_ind: 'N',
                    include_in_email_attachment: 'Y',
                    internal_name: 'line',
                    groupFilter: { operator: 'AND', rules: [] },
                    bar_order: 'alphabet',
                    bar_orientation: 'vertical',
                    colorFormationRulesData: [],
                    combine_tooltips: 'Y',
                    fillOpacity: 0.75,
                    queryAgainstEntireDataset: 'N',
                    customYAxis: 'N',
                    dataQueries: {
                        table: {
                            resultFieldAggregations: {},
                            resultFields: [],
                            sort: [],
                        },
                    },
                    display_label: 'N',
                    measureFields: [],
                    merge_small_label: 'Other',
                    minY: null,
                    maxY: null,
                    series: 'single',
                    show_all_series: 'Y',
                    stacking: 'none',
                    group_field: '',
                    system_other_grouping_label: '__system_other_grouping',
                    show_top_n_dir: 'DESC',
                    show_top_n_series: 5,
                    x_axis: {
                        field: '',
                        title: '',
                        value_type: 'numeric',
                        defaultZoom: '',
                        display_mask_id: '0',
                    },
                    y_axis: [{ field: '', title: '', value_type: 'numeric', defaultZoom: '', display_mask_id: '0' }],
                    isOnCreation: true,
                    drillTo: [],
                },
            },
            // datasetRendererBlockId: number;
            displayOrder: nextSortOrder,
            // elementId: number;
            uid: uid,
        };
    }
}

export interface LineChartInitialDataResponse {
    dataResults: { [key: string]: [AssocArray<string>]; table: [AssocArray<string>] };
}

export interface AdditionalContentSettingsLineChart extends IComponentProperties {
    page: number;
}

export interface MeasureFieldDisplayType {
    name: string;
    label: string;
    icon: IconType;
}

export const AllowedMeasureFieldDisplayTypes: Array<MeasureFieldDisplayType> = [
    { name: 'line', label: 'Line', icon: { type: 'mi', value: 'line-chart-with-arrow' } },
    { name: 'column', label: 'Bar', icon: { type: 'mi', value: 'bar-chart' } },
    { name: 'area', label: 'Area', icon: { type: 'mi', value: 'line-chart' } },
];

export interface MeasureFieldAggregationType {
    name: string;
    label: string;
    value_type: DatasetFieldValueType;
}

export type BarOrderType = 'alphabet' | 'lowest' | 'highest';
export type BarOrientationType = 'horizontal' | 'vertical';
export type LineChartSeriesType = 'single' | 'multiple';

export const AllowedMeasureFieldAggregations: Array<MeasureFieldAggregationType> = [
    { label: 'Sum', name: 'Sum', value_type: 'numeric' },
    { label: 'Avg', name: 'Avg', value_type: 'numeric' },
    { label: 'Min', name: 'Min', value_type: 'numeric' },
    { label: 'Max', name: 'Max', value_type: 'numeric' },
    { label: 'Count', name: 'Count', value_type: 'numeric' },
    { label: 'Count Distinct', name: 'Count Distinct', value_type: 'numeric' },
    { label: 'Count', name: 'Count', value_type: 'text' },
    { label: 'Count Distinct', name: 'Count Distinct', value_type: 'text' },
    { label: 'Count', name: 'Count', value_type: 'datetime' },
    { label: 'Count Distinct', name: 'Count Distinct', value_type: 'datetime' },
    { label: 'Min', name: 'Min', value_type: 'datetime' },
    { label: 'Max', name: 'Max', value_type: 'datetime' },
];

export interface ComponentSettingsChartDataQueriesTable {
    resultFieldAggregations: AssocArray<string>;
    resultFields: Array<string>;
    sort?: Array<{ field: string; dir: OrderDirection }>;
    sortFields?: Array<{ field: string; dir: OrderDirection }>;
    top?: {
        field: string;
        count: number;
        label: string;
        sort_field?: string;
        dir: OrderDirection;
    };
    group?: {
        operator: 'AND' | 'OR';
        rules: Array<{
            field: string;
            condition: string;
            data: string;
        }>;
    };
    distinct?: boolean;
    isExternalFilter?: boolean;
}

export interface ComponentSettingsChartDataQueries {
    [key: string]: ComponentSettingsChartDataQueriesTable;
    table: ComponentSettingsChartDataQueriesTable;
}

export interface ComponentSettingsLineChartAxis {
    field: string;
    title: string;
    value_type: DatasetFieldValueType;
    defaultZoom: string;
    display_mask_id: string;
}

export interface ComponentSettingsLineChart extends ChartComponentSettings {
    uid: string;
    bar_order: BarOrderType;
    bar_orientation: BarOrientationType;
    colorFormationRulesData: Array<any>;
    combine_tooltips: YNValues;
    // component_name: 'Line/Bar/Area';
    customYAxis: YNValues;

    display_label: YNValues;
    fillOpacity: number;
    // groupFilter: { operator: 'AND'; rules: [] };

    // internal_name: 'line';
    // is_preview_ind: 'N';
    // localPalette: {};

    measureFields: Array<ComponentSettingsChartMeasureField>;
    // merge_small_color: '#0062fe';
    merge_small_label: string;
    minY: number | null;
    maxY: number | null;
    queryAgainstEntireDataset: YNValues;
    // report_multiple_units_label: 'rows';
    // report_no_units_label: 'No rows';
    // report_single_unit_label: 'row';
    series: LineChartSeriesType;
    show_all_series: YNValues;
    // show_title: 'N';
    show_top_n_dir: OrderDirection;
    show_top_n_series: number;
    stacking: string;
    group_field?: string;
    system_other_grouping_label: string;
    x_axis: ComponentSettingsLineChartAxis;
    y_axis?: Array<ComponentSettingsLineChartAxis>;
}
export interface ChartComponentSettings extends ComponentSettings {
    dataQueries: ComponentSettingsChartDataQueries;
    drillTo?: Array<DrillTo>;
}

export interface MeasureFieldSortingContextType {
    elementProps: FormDataAPIType;
    selectField: <T extends ComponentSettingsChartMeasureField>(field: T) => void;
    removeField: <T extends ComponentSettingsChartMeasureField>(field: T) => void;
    onSortEnd: (oldIndex: number, newIndex: number) => void;
    onUpdateMeasureField: <T extends ComponentSettingsChartMeasureField>(newField: T) => void;
    datasetFields: Array<DatasetField>;
}

export function fieldToChartMeasuredField(field: DatasetField): ComponentSettingsChartMeasureField {
    const aggregations = AllowedMeasureFieldAggregations.filter((f) => f.value_type == field.value_type);

    // TODO:
    return {
        dataset_column_id: field.dataset_column_id,
        dataset_filter_column_id: field.dataset_filter_column_id,
        dataset_filter_id: field.dataset_filter_id,
        dataset_id: field.dataset_id,
        aggregation_function: aggregations.length ? aggregations[0].name : '',
        color: '',
        label: field.reference_name,
        column_name: field.column_name,
        display_mask_id: field.display_mask_id ?? '0',
        display_type: 'line',
        reference_name: field.reference_name,
        reference_name_escaped: field.reference_name_escaped,
        uId: generateMeasureFieldId(),
        value_type: field.value_type,
        y_axis: 'left',
    };
}
